import { Injectable } from "@angular/core";
import { FaqCategory } from "@shared/enums";

@Injectable({
  providedIn: "root",
})
export class FaqService {
  getFaqsByCategory(category: FaqCategory) {
    if (category === FaqCategory.IMAX_STRATEGY) {
      return this.getImaxStrategyFaqs();
    } else if (category === FaqCategory.PLANS) {
      return this.getPlansFaqs();
    } else {
      return this.getOfflineFaqs();
    }
  }

  getImaxStrategyFaqs() {
    return [
      {
        category: "compass imax guide faqs",
        faqs: [
          {
            question: "What is IMAX, VMAX, and PROJ?",
            answer: {
              paragraphs: [
                {
                  title: "IMAX",
                  text: `IMAX is one of three limitations used to restrict monthly maximums for all pilots except those on Small
                            Narrowbody aircraft (none currently in service at AA). Pilots are limited to the lower of IMAX, the limit on
                            voluntary flying (VMAX), or FAR limits.`,
                },
                {
                  title: "VMAX",
                  text: `VMAX is the maximum PROJ allowed by the company for a given month. 
                            If a pilot “Posted” trip makes it through the batch run without being picked up or dropped, 
                            pilots can pick up those trips, even if the pilot is at or above VMAX, in real time by using the shopping cart 
                            and accepting the pop-up that asks if a pilot is willing to pick up above VMAX.`,
                },
                {
                  title: "PROJ",
                  text: "PROJ is the number of credited hours a pilot is projected to recveive for a given month.",
                },
              ],
            },
            ref: "IMX-00-001",
          },
          {
            question: "Why is the target IMAX for an IMAX low month 14 hrs and not 0 hrs?",
            answer: {
              description:
                "The target IMAX for an IMAX low month is 14 hours because PBS will solve with 0 trips if a pilot has an IMAX of 14 or less.",
            },
            ref: "IMX-00-002",
          },
          {
            question: "Can I use Pick-up/Makeup if I am IMAX limited?",
            answer: {
              description: `Not above your IMAX limits. Pick-up flying (unless designated as Premium) is credited to your PROJ. If
                    your PROJ is IMAX limited, you can only fly for Premium (PM/PR) or Above Guarantee (OG), which is
                    not credited to your PROJ.`,
            },
            ref: "IMX-00-003",
          },
          {
            question: "Can I fly Over Guarantee while on Reserve and IMAX limited?",
            answer: {
              description: `Yes, because OG sequences are pay, no credit above Reserve Guarantee (CBA Section 15.C.4.b and
                        Q&A 15-11).`,
            },
            ref: "IMX-00-004",
          },
          {
            question: "Can I be reassigned over IMAX?",
            answer: {
              description: `2023 CBA Section 15.N. does allow a Lineholder to be reassigned over IMAX. If this occurs, all flying
                    done above IMAX will be paid at premium rates, and the pilot will be removed with pay, no credit, from
                    some or all of a later sequence in order to bring the pilot’s PROJ below IMAX.`,
            },
            ref: "IMX-00-005",
          },
          {
            question: "What happens if I exceed IMAX as a result of an overfly?",
            answer: {
              description: `Nothing. Your IMAX will be exceeded for the month, and the following month’s IMAX will be adjusted as
                    applicable.`,
            },
            ref: "IMX-00-006",
          },
          {
            question: `What happens to my guarantee if I'm IMAX limited 
                and I chose to pick up a trip of higher value that is below 
                the lower end of the LCW?`,
            answer: {
              description: `Your guarantee will be lowered by the value of your reduced PROJ. For example, a pilot has an IMAX
                    limit of 40, is awarded 36 hours of credited flying and lower end of the LCW (guarantee) is 75. If the pilot
                    trades a 3-day trip worth 16:00 hours for another 3-day trip worth 15:45 (15 minutes less), their new
                    guarantee will be 74:45.`,
              paragraphs: [
                {
                  text: `NOTE: Programming disparity currently pays this value above the LCW pay. Pilots choosing to pick up
                            additional credited flying above their PBS awarded value do so at their own option.`,
                },
              ],
            },
            ref: "IMX-00-007",
          },
          {
            question: "Do other carriers have IMAX?",
            answer: {
              description: `Yes. SWA, DAL and UAL have limiting methods similar to the IMAX.`,
              paragraphs: [
                {
                  text: `DAL has a block hour limit that is very much like our IMAX; our current IMAX was designed to mimic
                        the DAL limiter.`,
                },
                {
                  text: `SWA has a limiter known as the 365 Day Flight Time max that if exceeded does not pay the pilot
                        via a sequence protection method. SWA hours are monitored, and a pilot's monthly flying is scaled
                        back to prevent limits from being exceeded, and, if exceeded, a SWA pilot cannot bid a reserve line
                        and collect the reserve guarantee.`,
                },
                {
                  text: `UAL monthly bids have a limiter called the Personal Block Cap that gives a 90-hour cap each month.`,
                },
                {
                  text: `Each airline has slight differences such as whether sick, vacation, training or displacements count toward
                    their limits. All of these programs attempt to limit pilots to approximately 90 hours/month.`,
                },
              ],
            },
            ref: "IMX-00-008",
          },
        ],
      },
    ];
  }

  getPlansFaqs() {
    return [
      {
        category: "managing a plan",
        faqs: [
          {
            question:
              "What is the purpose of having PVDs, Training, and Sick days in the details of a month within the plan?",
            answer: {
              description: `Having the ability to mark PVDs, Training, 
              and Sick days in the plan allows you to visualize your 
              road to success when it comes to achieving high proj months via the charts. 
              The marking of these days has no impact on the value of PROJ itself, 
              rather just gives you some situational awareness for what might be necessary to achieve the goal.`,
            },
            ref: "PLN-00-001",
          },
        ],
      },
      {
        category: "selecting a plan",
        faqs: [
          {
            question: "What is a custom plan?",
            answer: {
              description:
                "A custom plan is created using the default PROJ and default VMAX values given by the user. To achieve an IMAX run, set PROJ values to drive IMAX down below 14 hrs.",
            },
            ref: "PLN-01-001",
          },
          {
            question: "If I make a new plan, does it become the active plan?",
            answer: {
              description:
                "No, creating a new plan will always go into the standby plan. You must manually activate any standby plan to swap what is active and what is standby.",
            },
            ref: "PLN-01-002",
          },
          {
            question: "Can I edit a preset plan?",
            answer: {
              description:
                "Yes, once a preset plan is selected, you can edit the plan by changing the value of PROJ for any month.",
            },
            ref: "PLN-01-003",
          },
        ],
      },
    ];
  }

  getOfflineFaqs() {
    return [
      {
        category: "offline use",
        faqs: [
          {
            question: "How do I use the app offline?",
            answer: {
              description:
                'Before going offline, go to the home page and select "Save All for Offline".  This will save all the data needed to use the app offline. ',
            },
            ref: "OFF-00-001",
          },
          {
            question: "What features are unavailable offline?",
            answer: {
              description: "The following features are unavailable offline:",
              paragraphs: [
                {
                  text: "Standby Plan options",
                },
                {
                  text: "Saving any changes",
                },
                {
                  text: "Getting new data from the database",
                },
              ],
            },
            ref: "OFF-00-002",
          },
          {
            question: "How do I know if I am offline?",
            answer: {
              description: "If you are offline, the app will display a message at the top of the screen.",
            },
            ref: "OFF-00-003",
          },
        ],
      },
    ];
  }
}
